// 11 §4.2 `units` layer: body + overlays, outline, base marker beneath the feet (shape, not colour
// alone: circle for allies, diamond for enemies). Pivot is the bottom centre of the occupied cell
// (#309), or of the whole footprint for a huge body (docs/79: `size` squares a side, its 24 px
// frame drawn `size` times over with nearest filtering; docs/92: a commissioned huge body is its
// own art, drawn once, wreathed in its `HugeAura`). Dye
// ramps: `DyeRampNode` (§4.4) is not here yet; the CPU fallback is a no-op for the placeholder
// art. Playback tweens act on the container.
import Phaser from 'phaser';
import type { FallenView, Tile, UnitState } from '../../core/sim/types';
import type { AtlasManifest } from '../../config';
import { unitFrame } from '../assets';
import { FALLEN_ALPHA, FALLEN_SHADE, greyFrame, toppledFeet } from './CorpseLayer';
import { StatusAura, auraQuality, type AuraQuality } from '../status/StatusAura';
import { sizeOf } from '../../core/sim/body';
import { feetPoint as feetOf } from '../../core/playback/walk';
import { bodyOriginY, bodyScale, flipToFace, groundStand, hugeArtByKey } from '../hugeArt';
import { HugeAura } from '../huge/HugeAura';
import { KinAura, kinLookOf } from './KinAura';
import type { PetEmoteDef } from '../../core/emote/catalogue';
import { REST, petPose, type PetPose } from '../../core/emote/petPose';
import { WaterLine } from '../water/WaterLine';
import { gaitKey } from '../../core/sim/water';

/** docs/102 FL3: the depth every figure in the air sits above (the units layer's ground figures sort by their feet, well under it). */
export const AIR_DEPTH = 1_000_000;

export class UnitSprite extends Phaser.GameObjects.Container {
  readonly sprite: Phaser.GameObjects.Image;
  readonly marker: Phaser.GameObjects.Graphics;
  private outlines: Phaser.GameObjects.Image[] = [];
  tile: Tile;
  /** docs/79: the body's side in squares (1 for most; 2 or 3 for a huge foe). */
  readonly size: number;
  /** docs/50: it fell this playback — it stays lying until the next board draws it as a body. */
  fallen = false;
  /** How many times its texture is drawn over (`bodyScale`): an Oryx frame `size` times, commissioned art once. */
  private readonly texScale: number;
  /** docs/92: a commissioned huge body's embers, dust, glow and ground. */
  readonly huge?: HugeAura;
  /** The texture it was drawn from (a fall swaps in a grey copy): which way its art faces. */
  private artKey: string;
  /** docs/102 FL3: how high it is off the ground (board px) while it flies or is carried; 0 standing. */
  lift = 0;
  /** docs/102 FL5: the flier carrying it off: it is drawn just behind its talons. */
  heldBy?: UnitSprite;
  /** docs/109: a small people's own standing look. */
  readonly kin?: KinAura;
  private readonly cell: number;

  constructor(scene: Phaser.Scene, readonly unit: UnitState, manifest: AtlasManifest, quality?: AuraQuality) {
    super(scene, 0, 0);
    const cell = manifest.cell;
    this.cell = cell;
    this.size = sizeOf(unit);
    const { key, frame } = unitFrame(manifest, unit, (k, f) => scene.textures.exists(k) && scene.textures.get(k).has(f));
    this.artKey = key;
    const n = bodyScale(key, this.size);
    const oy = bodyOriginY(key);
    this.texScale = n;
    this.tile = { ...unit.tile };

    this.marker = scene.add.graphics();
    this.drawMarker(cell);
    this.marker.setY(-this.stand);
    this.add(this.marker);

    // 1 px outline: four FILL-tinted copies behind the body (Phaser 4: setTint + setTintMode(FILL)).
    const outlineColour = unit.faction === 'hero' ? 0x0b1020 : 0x1a0808;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const o = scene.add.image(dx * n, dy * n, key, frame).setOrigin(0.5, oy).setScale(n).setTint(outlineColour).setTintMode(Phaser.TintModes.FILL);
      o.setData('home', [dx * n, dy * n]);
      this.outlines.push(o);
      this.add(o);
    }
    this.sprite = scene.add.image(0, 0, key, frame).setOrigin(0.5, oy).setScale(n);
    this.add(this.sprite);
    const art = hugeArtByKey(key);
    if (art) this.huge = new HugeAura({ scene, container: this, body: this.sprite, bodies: () => this.bodies, cell, size: this.size, art, unitId: unit.unitId }, quality);
    // docs/109: a small people that is not flesh stands like it (a ghost half there, a flame flickering)
    const look = art ? undefined : kinLookOf(unit.appearance, unit.faction === 'enemy');
    if (look) this.kin = new KinAura({ scene, container: this, body: this.sprite, bodies: () => this.bodies, cell, fallen: () => this.fallen }, look, quality ?? auraQuality());
    this.placeAt(unit.tile, cell);
    // docs/69: its statuses — a look on the figure for each, and their badges over its head
    // (docs/66's marks are statuses too: a board without `statuses` still carries them)
    this.aura = new StatusAura({ scene, container: this, body: this.sprite, bodies: this.bodies, cell: cell * this.size }, quality);
    this.aura.set(unit.statuses ?? unit.marks?.map((kind) => ({ kind, magnitude: 0 })));
    // docs/93 AQ10: where it meets the water (or hovers over the ground), by its gait
    this.water = new WaterLine({
      scene, container: this, bodies: () => this.bodies, body: this.sprite, cell, size: this.size, originY: () => bodyOriginY(this.artKey), gait: gaitKey(unit.gait),
      statuses: () => this.aura.drawn(), lift: () => this.lift, fallen: () => this.fallen,
    });
    scene.add.existing(this);
  }

  /**
   * docs/145 KH6: a hero drawn before its kit look had baked shows its Oryx frame; once the look is a texture, it
   * swaps to it (standing figures only: a fallen one keeps its grey body).
   */
  useKit(manifest: AtlasManifest): void {
    if (this.fallen) return;
    const { key, frame } = unitFrame(manifest, this.unit, (k, f) => this.scene.textures.exists(k) && this.scene.textures.get(k).has(f));
    if (key === this.artKey || !key.startsWith('hk:')) return;
    const flip = this.sprite.flipX;
    this.artKey = key;
    const oy = bodyOriginY(key);
    this.sprite.setTexture(key, frame).setOrigin(0.5, oy);
    for (const o of this.outlines) o.setTexture(key, frame).setOrigin(0.5, oy);
    this.sprite.setFlipX(flip);
    for (const o of this.outlines) o.setFlipX(flip);
    this.marker.setY(this.lift - this.stand);
    this.aura.relayout();
  }

  /** docs/93 AQ10: its waterline, submerged back, surfacing and hover. */
  readonly water: WaterLine;

  /** docs/69: the unit's statuses on the board. */
  readonly aura: StatusAura;

  /** A party figure's base ring is in its player's colour (gold until it is known). */
  private ringColour = 0xffd166;

  setRingColour(colour: number): void {
    if (colour === this.ringColour) return;
    this.ringColour = colour;
    this.drawMarker(this.cell);
  }

  private drawMarker(cell: number): void {
    const g = this.marker;
    g.clear();
    const r = Math.floor((cell * this.size) / 2) - 3;
    if (this.unit.faction === 'enemy') {
      g.lineStyle(1, 0xff6b6b, 0.9);
      g.strokePoints([new Phaser.Math.Vector2(0, -3), new Phaser.Math.Vector2(r, -1), new Phaser.Math.Vector2(0, 1), new Phaser.Math.Vector2(-r, -1)], true, true);
      if (this.unit.leader) g.strokePoints([new Phaser.Math.Vector2(0, -5), new Phaser.Math.Vector2(r + 2, -1), new Phaser.Math.Vector2(0, 3), new Phaser.Math.Vector2(-r - 2, -1)], true, true);
    } else {
      g.lineStyle(1, this.ringColour, 0.9);
      g.strokeEllipse(0, -1, r * 2, 4);
    }
  }

  /** Board px its ground marks (base marker, shadow) sit above its square's floor (a kit hero's, docs/145). */
  get stand(): number { return groundStand(this.artKey); }

  /** The body and its outline copies (effects that move the figure but not its base marker). */
  get bodies(): Phaser.GameObjects.Image[] { return [...this.outlines, this.sprite]; }

  placeAt(tile: Tile, cell: number): void {
    this.tile = { ...tile };
    const p = feetOf(tile, this.size, cell);
    this.setPosition(p.x, p.y);
    // standing on a square is standing on the ground
    if (this.lift || this.heldBy) { this.setLift(0); this.heldBy = undefined; }
  }

  /**
   * docs/102 FL3: it is `px` off the ground (the caller puts the container that much above the
   * ground point). What it shows on the ground stays there: its base marker (dimmed as it rises,
   * the shadow takes over) and a huge body's ground.
   */
  setLift(px: number): void {
    this.lift = Math.max(0, px);
    this.marker.setY(this.lift - this.stand).setAlpha(1 - 0.7 * Math.min(1, this.lift / this.cell));
    this.huge?.setLift(this.lift);
  }

  /**
   * The units layer sorts by where figures stand on the ground (docs/35). docs/102 FL3: a figure in
   * the air is over every one on the ground, the air sorted the same way among itself; a carried
   * figure just behind its carrier.
   */
  standDepth(): number {
    if (this.heldBy) return this.heldBy.standDepth() - 0.5;
    return this.lift > 0 ? AIR_DEPTH + this.y + this.lift : this.y;
  }

  /** docs/79: where the container stands when the body's origin is `tile`. */
  feetAt(tile: Tile, cell: number): { x: number; y: number } { return feetOf(tile, this.size, cell); }

  setFacing(dir: 1 | -1): void {
    // docs/95: an Oryx frame faces east as drawn, a commissioned body as its art says (most face west)
    const flip = flipToFace(this.artKey, dir);
    this.sprite.setFlipX(flip);
    for (const o of this.outlines) o.setFlipX(flip);
  }

  /**
   * docs/50: it topples where it stood into the pose the sim gave its body — the figure turns about
   * its feet as it greys and fades, the base marker and outline go — so the next board's body
   * (CorpseLayer) takes over without a jump. `ms` 0 lays it down at once (a skipped playback).
   */
  fall(f: FallenView, cell: number, ms: number): void {
    this.fallen = true;
    this.aura.clear();
    this.huge?.fall(ms);
    const to = toppledFeet(f, cell, this.sprite.displayHeight);
    const parts = [this.marker, ...this.outlines];
    const lie = () => {
      const g = greyFrame(this.scene, this.sprite.texture.key, this.sprite.frame.name);
      this.sprite.setTexture(g.key, g.frame).setTint(FALLEN_SHADE).setAlpha(FALLEN_ALPHA);
    };
    if (ms <= 0) {
      this.setPosition(to.x, to.y).setAngle(to.angle);
      for (const o of parts) o.setAlpha(0);
      lie();
      return;
    }
    this.scene.tweens.add({ targets: parts, alpha: 0, duration: ms * 0.4 });
    this.scene.tweens.add({ targets: this.sprite, alpha: FALLEN_ALPHA, duration: ms });
    this.scene.tweens.add({ targets: this, x: to.x, y: to.y, angle: to.angle, duration: ms, ease: 'Quad.easeIn', onComplete: lie });
  }

  /**
   * docs/62: a pet emote's wiggle (a ranger's companion): the body and its outline squash, hop,
   * wobble or lean about their feet for `ms`, then stand as they were. Never a frame change.
   */
  wiggle(mood: PetEmoteDef['key'], lean: number, ms: number): void {
    this.wiggling?.stop();
    const parts = this.bodies;
    const home = parts.map((b): [number, number] => (b === this.sprite ? [0, 0] : (b.getData('home') as [number, number])));
    const n = this.texScale;
    const paint = (p: PetPose) => parts.forEach((b, i) => { const [hx, hy] = home[i]!; b.setScale(n * p.sx, n * p.sy).setPosition(hx + p.dx * n, hy + p.dy * n).setAngle(p.angle); });
    const st = { k: 0 };
    const tw = this.scene.tweens.add({
      targets: st, k: 1, duration: ms, onUpdate: () => paint(petPose(mood, st.k, lean)),
      onComplete: () => { paint(REST); this.wiggling = undefined; }, onStop: () => paint(REST),
    });
    this.wiggling = tw;
    // a new board kills every tween without its onComplete or onStop: stand as before regardless
    this.scene.time.delayedCall(ms + 50, () => { if (this.wiggling !== tw) return; this.wiggling = undefined; if (this.active) paint(REST); });
  }
  private wiggling?: Phaser.Tweens.Tween;

  /** §5.3: dead units dim 50 %. */
  setDimmed(dim: boolean): void { this.setAlpha(dim ? 0.5 : 1); }

  /** Playback left the container/body offset: snap back to the tile. */
  reset(tile: Tile, cell: number): void {
    this.placeAt(tile, cell);
    this.setLift(0); this.heldBy = undefined;
    this.setAlpha(1); this.setScale(1); this.setAngle(0);
    this.sprite.setPosition(0, 0).setAlpha(1).setScale(this.texScale);
    for (const o of this.outlines) { const [dx, dy] = o.getData('home') as [number, number]; o.setPosition(dx, dy).setScale(this.texScale); }
    this.sprite.clearTint(); this.sprite.setTintMode(Phaser.TintModes.MULTIPLY);
  }

  flash(ms = 90): void {
    this.sprite.setTint(0xffffff).setTintMode(Phaser.TintModes.FILL);
    this.scene.time.delayedCall(ms, () => { this.sprite.clearTint(); this.sprite.setTintMode(Phaser.TintModes.MULTIPLY); });
  }
}
